import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from './config';

function NetworkManagement() {
  const [networkData, setNetworkData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchNetworkData();
  }, []);

  const fetchNetworkData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/network`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNetworkData(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch network data' });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/network/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'network-template.xlsx');
      link.click();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to download template' });
    }
  };

  const uploadNetworkData = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select a file' });
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API_URL}/api/network/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ type: 'success', text: response.data.message });
      setSelectedFile(null);
      document.getElementById('fileInput').value = '';
      fetchNetworkData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const exportNetworkData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/network/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `network-data-${new Date().toISOString().split('T')[0]}.xlsx`);
      link.click();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export data' });
    }
  };

  return (
    <div className="fade-in">
      <h1 className="page-title">Network Management</h1>

      {message.text && (
        <div className={`message-toast ${message.type}`} style={{ position: 'static', marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {/* Action Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
        <div className="premium-card">
          <h3>Download Template</h3>
          <p style={{ color: 'var(--system-secondary-label)', marginBottom: '20px' }}>
            Excel template with correct columns
          </p>
          <button onClick={downloadTemplate} className="premium-button" style={{ width: '100%' }}>
            Download Template
          </button>
        </div>

        <div className="premium-card">
          <h3>Upload New ASCs</h3>
          <input
            type="file"
            id="fileInput"
            accept=".xlsx,.xls"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            className="premium-input"
            style={{ marginBottom: '10px' }}
          />
          <button 
            onClick={uploadNetworkData} 
            disabled={!selectedFile || uploading}
            className="premium-button success"
            style={{ width: '100%' }}
          >
            {uploading ? <span className="loading-spinner" style={{ width: 20, height: 20 }} /> : 'Upload & Add'}
          </button>
        </div>

        <div className="premium-card">
          <h3>Export Current Data</h3>
          <p style={{ color: 'var(--system-secondary-label)', marginBottom: '20px' }}>
            {networkData.length} ASCs in database
          </p>
          <button onClick={exportNetworkData} className="premium-button info" style={{ width: '100%' }}>
            Export Data
          </button>
        </div>
      </div>

      {/* Network Data Table */}
      <div className="premium-table-container">
        <h3>Current Network ({networkData.length} ASCs)</h3>
        {loading ? (
          <div className="premium-loading">
            <div className="loading-spinner" />
          </div>
        ) : (
          <table className="premium-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>ASP Name</th>
                <th>City</th>
                <th>State</th>
                <th>Pincode</th>
                <th>Zone</th>
                <th>ASM</th>
                <th>Added By</th>
                <th>Added On</th>
              </tr>
            </thead>
            <tbody>
              {networkData.slice(0, 10).map((asc) => (
                <tr key={asc.id}>
                  <td>{asc.id}</td>
                  <td><strong>{asc.asp_name}</strong></td>
                  <td>{asc.city}</td>
                  <td>{asc.state}</td>
                  <td>{asc.coverage_pincode}</td>
                  <td>
                    <span className="premium-badge">{asc.zone}</span>
                  </td>
                  <td>{asc.asm_name}</td>
                  <td>{asc.created_by || 'System'}</td>
                  <td>{new Date(asc.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {networkData.length > 10 && (
          <p style={{ textAlign: 'center', marginTop: '16px', color: 'var(--system-secondary-label)' }}>
            ... and {networkData.length - 10} more
          </p>
        )}
      </div>
    </div>
  );
}

export default NetworkManagement;